import { useState } from 'react';
import { useNavigate, useRevalidator } from 'react-router';
import { SegmentRulesSchema, type SegmentRules } from '@/types';
import { Button } from '@/components/ui/button';
import { api, errMsg } from '@/lib/api';
import { RuleBuilder } from '@/components/rule-builder';

const DEFAULT_RULES: SegmentRules = { all: [{ field: 'tier', op: 'in', value: ['free'] }], none: [{ field: 'suppressed', op: 'eq', value: true }] };

export function SegmentForm({
  initial,
}: {
  initial?: { id: string; name: string; description: string | null; kind: string; rules: unknown; system?: boolean };
}) {
  const navigate = useNavigate();
  const { revalidate } = useRevalidator();
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [kind, setKind] = useState<'dynamic' | 'static'>((initial?.kind as 'dynamic' | 'static') ?? 'dynamic');
  const parsedInitial = initial?.rules ? SegmentRulesSchema.safeParse(initial.rules) : null;
  const [rules, setRules] = useState<SegmentRules>(parsedInitial?.success ? parsedInitial.data : DEFAULT_RULES);
  const [showJson, setShowJson] = useState(false);
  const [json, setJson] = useState('');
  const [preview, setPreview] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function currentRules(): SegmentRules {
    const source = showJson ? JSON.parse(json) : rules;
    const parsed = SegmentRulesSchema.safeParse(source);
    if (!parsed.success) throw new Error(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '));
    return parsed.data;
  }

  async function doPreview() {
    setError(null);
    setBusy(true);
    try {
      const result = await api<{ count: number }>('/segments/preview', { method: 'POST', json: currentRules() });
      setPreview(result.count);
    } catch (err) {
      setError(errMsg(err, 'Invalid rules'));
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setError(null);
    setBusy(true);
    try {
      const body = { name, description: description || undefined, kind, rules: kind === 'dynamic' ? currentRules() : undefined };
      if (initial) {
        await api(`/segments/${initial.id}`, { method: 'PATCH', json: body });
      } else {
        const created = await api<{ id: string }>('/segments', { method: 'POST', json: body });
        navigate(`/segments/${created.id}`);
      }
      revalidate();
    } catch (err) {
      setError(errMsg(err, 'Save failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 rounded-lg border bg-white p-4 text-sm">
      <h2 className="text-base">{initial ? 'Edit segment' : 'New segment'}</h2>
      {initial?.system && <p className="text-xs text-muted-foreground">Default segment — rules can be edited, the segment cannot be deleted.</p>}
      <label className="block space-y-1">
        <span className="text-xs text-muted-foreground">Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} className="h-9 w-full rounded-md border px-2" />
      </label>
      <label className="block space-y-1">
        <span className="text-xs text-muted-foreground">Description</span>
        <input value={description} onChange={(e) => setDescription(e.target.value)} className="h-9 w-full rounded-md border px-2" />
      </label>
      <label className="block space-y-1">
        <span className="text-xs text-muted-foreground">Kind</span>
        <select value={kind} onChange={(e) => setKind(e.target.value as 'dynamic' | 'static')} className="h-9 w-full rounded-md border px-2" disabled={!!initial}>
          <option value="dynamic">Dynamic (rules, re-evaluated automatically)</option>
          <option value="static">Static (list you fill by CSV import / flows)</option>
        </select>
      </label>
      {kind === 'dynamic' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Rules</span>
            <button
              type="button"
              className="ff-link text-xs"
              onClick={() => {
                if (!showJson) setJson(JSON.stringify(rules, null, 2));
                else {
                  try {
                    const parsed = SegmentRulesSchema.parse(JSON.parse(json));
                    setRules(parsed);
                  } catch (e) {
                    setError(e instanceof Error ? e.message : 'Invalid JSON');
                    return;
                  }
                }
                setShowJson(!showJson);
              }}
            >
              {showJson ? 'Use builder' : 'Edit as JSON'}
            </button>
          </div>
          {showJson ? (
            <textarea value={json} onChange={(e) => setJson(e.target.value)} rows={14} className="w-full rounded-md border p-2 font-mono text-xs" />
          ) : (
            <RuleBuilder value={rules} onChange={setRules} />
          )}
        </div>
      )}
      <div className="flex items-center gap-2">
        {kind === 'dynamic' && (
          <Button type="button" variant="outline" size="sm" onClick={doPreview} disabled={busy}>
            Preview count
          </Button>
        )}
        <Button type="button" size="sm" onClick={save} disabled={busy || !name.trim()}>
          {initial ? 'Save' : 'Create'}
        </Button>
        {preview !== null && <span className="text-muted-foreground">{preview} contacts match</span>}
      </div>
      {error && (
        <p role="alert" className="rounded-md bg-red-50 p-2 text-xs text-red-800">
          {error}
        </p>
      )}
    </div>
  );
}
